import * as amplitude from "@amplitude/analytics-browser";
import { useRefSize } from "@redotech/react-util/size";
import { useTranslation } from "@redotech/redo-i18n";
import DotsHorizontalIcon from "@redotech/redo-icons/dots-horizontal.svg";
import DotsHorizontalFilledIcon from "@redotech/redo-icons/dots-horizontal_filled.svg";
import classNames from "classnames";
import {
  Fragment,
  ReactNode,
  memo,
  useCallback,
  useContext,
  useMemo,
  useState,
} from "react";
import { useNavigate } from "react-router-dom";
import { Flex } from "../../../flex";
import { Text } from "../../../text";
import * as darkModeCss from "../../../theme/component-colors-dark.module.css";
import { Tooltip } from "../../../tooltip/tooltip";
import type { RedoListItem } from "../../list/redo-list";
import { RedoListDropdown } from "../../list/redo-list-dropdown";
import { NavIconSwap } from "../common/redo-nav-icon-swap";
import * as navigationCss from "../redo-navigation.module.css";
import { LinkPrimaryTab, PrimaryNavIconOnlyContext } from "./redo-primary-nav";
import { PrimaryNavigationTab } from "./redo-primary-nav-tab";

const OVERFLOW_GAP_PX = 16; // --redo-spacing-xl
const DIVIDER_HEIGHT_PX = 1 + OVERFLOW_GAP_PX;

function dividerCount(
  visibleCount: number,
  sectionBoundaries: readonly number[],
): number {
  return sectionBoundaries.filter((b) => visibleCount > b).length;
}

/**
 * Measures the primary-nav tab column and decides how many tabs fit, given the
 * available height. The remainder is meant to collapse into a "More" popover.
 * Recomputes on container/tab resize, so it responds to window-height changes.
 *
 * @param sectionBoundaries flat indices where a divider precedes a tab; used to
 *   reserve vertical space for the divider lines in the fit calculation.
 */
export function usePrimaryNavOverflow(
  totalItemCount: number,
  sectionBoundaries: readonly number[],
) {
  const [containerRef, containerSize] = useRefSize<HTMLElement>();
  const [tabRef, tabSize] = useRefSize<HTMLElement>();

  const visibleCount = useMemo(() => {
    if (!containerSize || !tabSize || tabSize.height === 0) {
      return totalItemCount;
    }

    const slot = tabSize.height + OVERFLOW_GAP_PX;

    // Check if everything fits without a "more" button
    const allDividers = dividerCount(totalItemCount, sectionBoundaries);
    const totalHeight =
      totalItemCount * slot - OVERFLOW_GAP_PX + allDividers * DIVIDER_HEIGHT_PX;
    if (totalHeight <= containerSize.height) {
      return totalItemCount;
    }

    // Find the largest count that fits alongside the "more" button.
    const available = containerSize.height - slot + OVERFLOW_GAP_PX;
    const fits = (c: number) =>
      c * slot + dividerCount(c, sectionBoundaries) * DIVIDER_HEIGHT_PX <=
      available;

    for (let c = totalItemCount - 1; c >= 1; c--) {
      if (fits(c)) {
        return c;
      }
    }
    return 1;
  }, [containerSize, tabSize, totalItemCount, sectionBoundaries]);

  return {
    visibleCount,
    isOverflowing: visibleCount < totalItemCount,
    containerRef,
    tabRef,
  };
}

export function tabToListItem(
  tab: LinkPrimaryTab,
): RedoListItem<LinkPrimaryTab> {
  return {
    id: tab.id,
    type: "text" as const,
    text: tab.friendlyName,
    leadingItem: { type: "icon" as const, Icon: tab.UnfilledIcon },
    value: tab,
  };
}

export const PrimaryNavOverflowPopover = memo(
  function PrimaryNavOverflowPopover({
    overflowTabs,
    addSectionTabs = [],
    selectedTabId,
  }: {
    overflowTabs: LinkPrimaryTab[];
    /**
     * When the Add button itself has overflowed, the available products it
     * would have contained are inlined into the More menu under an "Add"
     * subheader instead of appearing as a single "Add" entry.
     */
    addSectionTabs?: LinkPrimaryTab[];
    selectedTabId: string | undefined;
  }) {
    const { t } = useTranslation();
    const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
    const [open, setOpen] = useState(false);
    const iconOnly = useContext(PrimaryNavIconOnlyContext);
    const navigate = useNavigate();

    const hasSelectedOverflow = overflowTabs.some(
      (tab) => tab.id === selectedTabId,
    );

    const handleOpen = useCallback(() => {
      setOpen(true);
    }, []);

    const listItems: RedoListItem<LinkPrimaryTab>[] = useMemo(() => {
      const items: RedoListItem<LinkPrimaryTab>[] =
        overflowTabs.map(tabToListItem);
      if (addSectionTabs.length > 0) {
        items.push({
          id: "add-section-header",
          type: "custom" as const,
          render: (
            <Text fontSize="xs" textColor="tertiary">
              {t("Add", { context: "navigation menu" })}
            </Text>
          ),
          useWrapper: false,
          value: null as unknown as LinkPrimaryTab,
        });
        for (const tab of addSectionTabs) {
          items.push(tabToListItem(tab));
        }
      }
      return items;
    }, [overflowTabs, addSectionTabs, t]);

    const handleItemSelected = useCallback(
      ({ item }: { item: RedoListItem<LinkPrimaryTab> }) => {
        const tab = item.value;
        if (!tab) {
          return;
        }
        amplitude.track("nav/sidebar-item-clicked", {
          section: tab.id,
          item: tab.id,
        });
        navigate(tab.href);
      },
      [navigate],
    );

    const moreButton = (
      <Flex
        align="center"
        as="button"
        className={classNames(
          navigationCss.primaryTab,
          hasSelectedOverflow && navigationCss.selected,
        )}
        data-testid="primary-nav-more"
        dir="column"
        gap="xxs"
        justify="center"
        onClick={handleOpen}
        ref={setAnchorEl}
      >
        <Flex
          align="center"
          className={navigationCss.primaryTabIconBox}
          justify="center"
        >
          <NavIconSwap
            FilledIcon={DotsHorizontalFilledIcon}
            isSelected={hasSelectedOverflow}
            size={20}
            UnfilledIcon={DotsHorizontalIcon}
          />
        </Flex>
        {!iconOnly && (
          <Text
            as="span"
            fontSize="xxs"
            textColor={hasSelectedOverflow ? "primary" : "secondary"}
            textOverflow="ellipsis"
            whiteSpace="nowrap"
          >
            {t("More", { context: "navigation menu" })}
          </Text>
        )}
      </Flex>
    );

    return (
      <>
        {iconOnly ? (
          <Tooltip
            darkMode
            placement="right"
            title={t("More", { context: "navigation menu" })}
          >
            {moreButton}
          </Tooltip>
        ) : (
          moreButton
        )}
        <RedoListDropdown
          darkMode
          dropdownAnchor={anchorEl}
          dropdownOpen={open}
          dropdownPlacement="right-start"
          items={listItems}
          itemSelected={handleItemSelected}
          selectedItems={selectedTabId}
          selectionVariant="none"
          setDropdownOpen={setOpen}
          size="xs"
        />
      </>
    );
  },
);

export interface PrimaryNavGroup {
  id: string;
  tabs: LinkPrimaryTab[];
}

/**
 * General primary-nav column: a vertical icon rail of tab groups (separated by
 * dividers) that collapses overflowing tabs into a "More" popover when the
 * viewport is too short to show them all. Optional `header` and `footer` slots
 * render above the scrolling tab list and pinned at the bottom, respectively.
 *
 * The overflow math applies only to `groups`; `header`/`footer` are always
 * visible. Used by the admin nav and available to any nav that needs the same
 * responsive primary rail.
 */
export const RedoPrimaryNavRail = memo(function RedoPrimaryNavRail({
  groups,
  selectedTabId,
  header,
  footer,
  iconOnly = false,
  width = 72,
  "data-dd-action-name": actionName,
}: {
  "data-dd-action-name"?: string;
  groups: PrimaryNavGroup[];
  selectedTabId?: string;
  header?: ReactNode;
  footer?: ReactNode;
  iconOnly?: boolean;
  width?: number;
}) {
  const flatTabs = useMemo(
    () => groups.flatMap((group) => group.tabs),
    [groups],
  );

  const sectionBoundaries = useMemo(() => {
    const boundaries: number[] = [];
    let acc = 0;
    for (let i = 0; i < groups.length - 1; i++) {
      acc += groups[i]!.tabs.length;
      if (acc > 0) {
        boundaries.push(acc);
      }
    }
    return boundaries;
  }, [groups]);

  const boundarySet = useMemo(
    () => new Set(sectionBoundaries),
    [sectionBoundaries],
  );

  const { visibleCount, isOverflowing, containerRef, tabRef } =
    usePrimaryNavOverflow(flatTabs.length, sectionBoundaries);

  const visibleTabs = flatTabs.slice(0, visibleCount);
  const overflowTabs = flatTabs.slice(visibleCount);

  return (
    <PrimaryNavIconOnlyContext.Provider value={iconOnly}>
      <Flex
        align="center"
        bgColor="tertiary"
        className={classNames(darkModeCss.darkMode, navigationCss.primaryNav)}
        data-arbiter="RedoPrimaryNavRail"
        data-dd-action-name={actionName}
        dir="column"
        grow={1}
        justify="space-between"
        px="xs"
        py="xl"
        style={{ width, maxWidth: width }}
      >
        <Flex
          align="center"
          className={navigationCss.primaryNavTabSection}
          dir="column"
          gap="xl"
          grow={1}
          shrink={1}
        >
          {header}
          <Flex
            align="center"
            className={navigationCss.primaryNavTabSection}
            dir="column"
            gap="xl"
            grow={1}
            ref={containerRef}
            shrink={1}
          >
            {visibleTabs.map((tab, i) => (
              <Fragment key={tab.id}>
                {boundarySet.has(i) && (
                  <Flex className={navigationCss.primaryNavDivider} />
                )}
                {i === 0 ? (
                  <div ref={tabRef}>
                    <PrimaryNavigationTab
                      selectedTabId={selectedTabId}
                      tab={tab}
                    />
                  </div>
                ) : (
                  <PrimaryNavigationTab
                    selectedTabId={selectedTabId}
                    tab={tab}
                  />
                )}
              </Fragment>
            ))}
            {isOverflowing && (
              <PrimaryNavOverflowPopover
                overflowTabs={overflowTabs}
                selectedTabId={selectedTabId}
              />
            )}
          </Flex>
        </Flex>
        {footer && (
          <Flex align="center" dir="column" gap="xl" shrink={0}>
            {footer}
          </Flex>
        )}
      </Flex>
    </PrimaryNavIconOnlyContext.Provider>
  );
});
